import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const STORAGE_KEY = "nutrimart:favs";

type FavId = string | number;

const normalize = (raw: string): FavId => {
  // Keep UUIDs / non-numeric as string; numeric strings become numbers
  if (/^-?\d+$/.test(raw)) {
    const n = Number(raw);
    if (Number.isSafeInteger(n)) return n;
  }
  return raw;
};

/**
 * Tracks the user's favorited product IDs.
 * - Authenticated users: synced with `favorites` table (RLS-protected).
 * - Guests: persisted to localStorage.
 * Supports both numeric (demo catalog) and UUID (DB) product IDs.
 */
export function useFavorites() {
  const [favs, setFavs] = useState<FavId[]>(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map((v: any) => (typeof v === "number" ? v : normalize(String(v)))) : [];
    } catch {
      return [];
    }
  });
  const [userId, setUserId] = useState<string | null>(null);
  const loadedRef = useRef(false);

  // track auth
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setUserId(session?.user?.id ?? null);
      loadedRef.current = false;
    });
    return () => { sub.subscription.unsubscribe(); };
  }, []);

  // load from DB on login
  useEffect(() => {
    if (!userId) {
      loadedRef.current = true;
      return;
    }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("favorites")
        .select("product_id")
        .eq("user_id", userId);
      if (cancelled || error) { loadedRef.current = true; return; }
      const ids: FavId[] = (data || []).map((r: any) => normalize(String(r.product_id)));
      // merge any guest favs into DB on first sync
      const local = favs;
      const dbKeys = new Set(ids.map(String));
      const toAdd = local.filter(id => !dbKeys.has(String(id)));
      const merged: FavId[] = [...ids];
      for (const id of local) if (!dbKeys.has(String(id))) merged.push(id);
      if (toAdd.length) {
        await supabase.from("favorites").insert(toAdd.map(pid => ({
          user_id: userId, product_id: String(pid)
        })));
      }
      setFavs(merged);
      loadedRef.current = true;
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // persist guest favs
  useEffect(() => {
    if (userId) return;
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(favs)); } catch {}
  }, [favs, userId]);

  const toggle = useCallback(async (pid: FavId) => {
    const key = String(pid);
    const wasFav = favs.some((x) => String(x) === key);
    setFavs((prev) => (prev.some((x) => String(x) === key) ? prev.filter((x) => String(x) !== key) : [...prev, pid]));
    if (!userId) return;
    if (wasFav) {
      await supabase.from("favorites").delete().eq("user_id", userId).eq("product_id", key);
    } else {
      await supabase.from("favorites").insert({ user_id: userId, product_id: key });
    }
  }, [favs, userId]);

  const isFav = useCallback((pid: FavId) => favs.some((x) => String(x) === String(pid)), [favs]);

  return { favs, setFavs, toggle, isFav };
}
